import { useState, type FormEvent } from 'react';
import { parties, requests, sourcing } from '../../api/endpoints';
import type { FulfillmentOption, Party, RequestItem } from '../../api/types';
import { Card, Column, DataTable, Empty, ErrorBox, Field, fmtMoney, label, StatusBadge } from '../../components/ui';
import { useAsync } from '../../hooks';
import type { CardData } from '../RequestCardPage';

const DOC_TYPES = ['unknown', 'ss', 'ds', 'refusal_letter', 'aip', 'sgr', 'other'];
const CURRENCIES = ['RUB', 'USD', 'EUR', 'CNY'];

function AddItemForm({ requestId, requestRevision, onDone }: { requestId: string; requestRevision?: number; onDone: () => void }) {
  const allParties = useAsync<Party[]>(() => parties.list(), []);
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('unknown');
  const [country, setCountry] = useState('');
  const [currency, setCurrency] = useState('RUB');
  const [payerId, setPayerId] = useState('');
  const [applicantId, setApplicantId] = useState('');
  const [manufacturerId, setManufacturerId] = useState('');
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [tnved, setTnved] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await requests.addItem(requestId, {
        title, document_type: docType,
        country_of_entry: country || undefined, currency, revision: requestRevision,
        payer_id: payerId || undefined, applicant_id: applicantId || undefined,
        manufacturer_id: manufacturerId || undefined,
        product_name: productName || undefined,
        product_description: productDescription || undefined,
        tnved: tnved || undefined,
      });
      setTitle(''); setCountry(''); setPayerId(''); setApplicantId(''); setManufacturerId('');
      setProductName(''); setProductDescription(''); setTnved('');
      onDone();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };

  return (
    <form className="inline-form" onSubmit={submit}>
      <ErrorBox error={err} />
      <div className="form-row">
        <Field label="Название позиции"><input value={title} onChange={(e) => setTitle(e.target.value)} required /></Field>
        <Field label="Тип документа">
          <select value={docType} onChange={(e) => setDocType(e.target.value)}>
            {DOC_TYPES.map((d) => <option key={d} value={d}>{label(d)}</option>)}
          </select>
        </Field>
        <Field label="Страна ввоза"><input value={country} onChange={(e) => setCountry(e.target.value)} maxLength={2} placeholder="RU" /></Field>
        <Field label="Валюта">
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
      </div>
      <div className="form-row">
        <Field label="Плательщик">
          <select value={payerId} onChange={(e) => setPayerId(e.target.value)}>
            <option value="">—</option>
            {(allParties.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Заявитель">
          <select value={applicantId} onChange={(e) => setApplicantId(e.target.value)}>
            <option value="">—</option>
            {(allParties.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Изготовитель">
          <select value={manufacturerId} onChange={(e) => setManufacturerId(e.target.value)}>
            <option value="">—</option>
            {(allParties.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      </div>
      <div className="form-row">
        <Field label="Продукция"><input value={productName} onChange={(e) => setProductName(e.target.value)} /></Field>
        <Field label="ТН ВЭД"><input value={tnved} onChange={(e) => setTnved(e.target.value)} /></Field>
      </div>
      <Field label="Техническое описание">
        <textarea value={productDescription} onChange={(e) => setProductDescription(e.target.value)} rows={2} />
      </Field>
      <div className="form-actions">
        <button className="btn primary" disabled={busy || !title}>{busy ? 'Добавление…' : 'Добавить позицию'}</button>
      </div>
    </form>
  );
}

function AddOptionForm({ item, onDone }: { item: RequestItem; onDone: () => void }) {
  const [country, setCountry] = useState('');
  const [scheme, setScheme] = useState('');
  const [seriesOrBatch, setSeriesOrBatch] = useState('');
  const [applicantMode, setApplicantMode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await sourcing.createOption({
        item: item.id, item_revision: item.revision, country, scheme,
        series_or_batch: seriesOrBatch, applicant_mode: applicantMode,
      });
      setCountry(''); setScheme(''); setSeriesOrBatch(''); setApplicantMode('');
      onDone();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };

  return (
    <form className="inline-form" onSubmit={submit}>
      <ErrorBox error={err} />
      <div className="form-row">
        <Field label="Страна"><input value={country} onChange={(e) => setCountry(e.target.value)} maxLength={2} placeholder="CN" /></Field>
        <Field label="Схема"><input value={scheme} onChange={(e) => setScheme(e.target.value)} placeholder="например, 1Д/3Д" /></Field>
        <Field label="Серия/партия"><input value={seriesOrBatch} onChange={(e) => setSeriesOrBatch(e.target.value)} /></Field>
        <Field label="Заявитель"><input value={applicantMode} onChange={(e) => setApplicantMode(e.target.value)} placeholder="режим заявителя" /></Field>
      </div>
      <div className="form-actions">
        <button className="btn primary" disabled={busy}>{busy ? 'Создание…' : 'Добавить вариант'}</button>
      </div>
    </form>
  );
}

// Режим выпуска позиции (Stage 4 Fix1): '' | 'batch' | 'series'.
// Обязателен для генерации DOCX — фиксируется в снимке заявки.
const PRODUCTION_MODES: Array<{ value: string; title: string }> = [
  { value: '', title: '—' },
  { value: 'batch', title: 'Партия' },
  { value: 'series', title: 'Серийное производство' },
];

function ProductionModeSelect({ item, onChanged }: { item: RequestItem; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const change = async (value: string) => {
    setBusy(true); setErr(null);
    try {
      await requests.updateItem(item.id, { production_mode: value, revision: item.revision });
      onChanged();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };

  return (
    <Field label="Режим выпуска"
           hint="Сохраняется при выборе. Если режим менялся после снимка — снимите новый снимок.">
      <select value={item.production_mode ?? ''} disabled={busy}
              onChange={(e) => { void change(e.target.value); }}>
        {PRODUCTION_MODES.map((m) => <option key={m.value} value={m.value}>{m.title}</option>)}
      </select>
      {err ? <span className="muted" style={{ fontSize: 12 }}>{err instanceof Error ? err.message : String(err)}</span> : null}
    </Field>
  );
}

function SubmitCalcButton({ option, onDone }: { option: FulfillmentOption; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const run = async () => {
    const question = window.prompt('Вопрос для технической квалификации (submit-for-calculation):',
      'Проверить применимость схемы и требования к документам');
    if (question === null) return;
    setBusy(true); setErr(null);
    try {
      await sourcing.submitForCalculation(option.id, question, option.revision);
      onDone();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };

  return (
    <span>
      <button className="btn small" onClick={run} disabled={busy}>
        {busy ? 'Передача…' : 'На просчёт'}
      </button>
      {err ? <div className="muted" style={{ fontSize: 12 }}>{err instanceof Error ? err.message : String(err)}</div> : null}
    </span>
  );
}

function CompleteItemButton({ item, onDone }: { item: RequestItem; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const run = async () => {
    setBusy(true); setErr(null);
    try {
      await requests.completeItem(item.id, item.revision);
      onDone();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  return (
    <span>
      <button className="btn small" data-action="complete-item" onClick={run} disabled={busy}>
        {busy ? 'Завершение…' : 'Завершить позицию'}
      </button>
      {err ? <div className="muted" style={{ fontSize: 12 }}>{err instanceof Error ? err.message : String(err)}</div> : null}
    </span>
  );
}

function ItemBlock({ item, options, onChanged }: { item: RequestItem; options: FulfillmentOption[]; onChanged: () => void }) {
  const [showOptionForm, setShowOptionForm] = useState(false);
  const itemOptions = options.filter((o) => o.item === item.id);

  const cols: Column<FulfillmentOption>[] = [
    { key: 'country', title: 'Страна', render: (r) => r.country || '—' },
    { key: 'scheme', title: 'Схема', render: (r) => r.scheme || '—' },
    { key: 'series_or_batch', title: 'Серия/партия', render: (r) => r.series_or_batch || '—' },
    { key: 'applicant_mode', title: 'Заявитель', render: (r) => r.applicant_mode || '—' },
    { key: 'status', title: 'Статус', render: (r) => <StatusBadge value={r.status} /> },
    { key: 'actions', title: '', render: (r) => <SubmitCalcButton option={r} onDone={onChanged} /> },
  ];

  return (
    <Card
      title={`Позиция: ${item.title}`}
      actions={
        <>
          <StatusBadge value={item.status} />
          {item.status === 'active' && <CompleteItemButton item={item} onDone={onChanged} />}
          <button className="btn small" onClick={() => setShowOptionForm((v) => !v)}>
            {showOptionForm ? 'Скрыть' : 'Добавить вариант'}
          </button>
        </>
      }
    >
      <div className="kv-grid" style={{ marginBottom: 8 }}>
        <div className="kv"><span className="kv-key">Тип документа</span><span className="kv-value">{label(item.document_type)}</span></div>
        <div className="kv"><span className="kv-key">Страна ввоза</span><span className="kv-value">{item.country_of_entry || '—'}</span></div>
        <div className="kv"><span className="kv-key">Согласованная цена</span><span className="kv-value">{fmtMoney(item.agreed_sale, item.currency)}</span></div>
      </div>
      <div className="form-row" style={{ marginBottom: 8 }}>
        <ProductionModeSelect item={item} onChanged={onChanged} />
      </div>

      <h3 style={{ fontSize: 13, margin: '8px 0' }}>Варианты исполнения</h3>
      {itemOptions.length === 0 && !showOptionForm && <Empty text="Вариантов нет — добавьте первый" />}
      {itemOptions.length > 0 && <DataTable columns={cols} rows={itemOptions} />}
      {showOptionForm && <AddOptionForm item={item} onDone={() => { setShowOptionForm(false); onChanged(); }} />}
    </Card>
  );
}

export function ItemsTab({ data }: { data: CardData }) {
  const [showItemForm, setShowItemForm] = useState(false);
  return (
    <div>
      <div className="toolbar">
        <button className="btn primary" onClick={() => setShowItemForm((v) => !v)}>
          {showItemForm ? 'Скрыть форму' : 'Добавить позицию'}
        </button>
      </div>
      {showItemForm && <AddItemForm requestId={data.request.id} requestRevision={data.request.revision} onDone={() => { setShowItemForm(false); data.reload(); }} />}
      {data.items.length === 0
        ? <Card><Empty text="Позиций нет" /></Card>
        : data.items.map((item) => (
          <ItemBlock key={item.id} item={item} options={data.options} onChanged={data.reload} />
        ))}
    </div>
  );
}
